import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Stack,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import { formatNumber } from "../../utils/constant";
import { Skeleton } from "@mui/material";

const StyledCard = styled(Card)(() => ({
  borderRadius: '16px',
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  border: '1px solid #f1f5f9',
  transition: 'all 0.2s ease-in-out',
  height: '100%',
  '&:hover': {
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    transform: 'translateY(-2px)',
  },
}));

const MetricCard = ({
  title,
  value,
  change,
  changeType,
  icon,
  color,
  subtitle,
}) => {
  const isPositive = changeType === "positive";

  return (
    <StyledCard>
      <CardContent
        sx={{
          p: 1.5,
          height: 150,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {/* Top Row */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography
            sx={{
              fontSize: "2rem",
              fontWeight: 700,
              color: "#424242",
            }}
          >
            {title}
          </Typography>

          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: 'transparent',
              color,
            }}
          >
            {icon}
          </Avatar>
        </Stack>

        {/* Value */}
        <Typography
          sx={{
            fontSize: 28,
            fontWeight: 700,
            color: "#020817",
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>

        {/* Bottom */}
        <Box sx={{ minHeight: 24 }}>
          {change ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                size="small"
                label={change}
                sx={{
                  height: 22,
                  fontSize: 12,
                  fontWeight: 600,
                  bgcolor: isPositive ? "#dcfce7" : "#fee2e2",
                  color: isPositive ? "#16a34a" : "#dc2626",
                }}
              />
              <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                vs last month
              </Typography>
            </Stack>
          ) : subtitle ? (
            <Typography sx={{ fontWeight: 400, fontSize: "1.4rem", color: "#424242" }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export const PlatformDashboardCards = ({
  loading = false,
  activeCustomers = 0,
  totalApiCalls = 0,
  totalCreditConsumed = 0,
}) => {
  return (
    <Box>
      <Grid container spacing={3}>
        {loading ? (
          [1, 2, 3].map((_, index) => (
            <Grid item xs={12} sm={6} lg={4} key={index}>
              <Skeleton
                variant="rounded"
                height={150}
                animation="wave"
              />
            </Grid>
          ))
        ) : (
          <>
            <Grid item xs={12} sm={6} lg={4}>
              <MetricCard
                title="Active Customers"
                value={activeCustomers}
                changeType="positive"
                icon={<PeopleAltOutlinedIcon sx={{ fontSize: "2.25rem" }} />}
                color="#6366f1"
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={4}>
              <MetricCard
                title="Total API Calls"
                value={formatNumber(totalApiCalls)}
                icon={<TimelineOutlinedIcon sx={{ fontSize: "2.25rem" }} />}
                color="#06b6d4"
                subtitle="Across all customers"
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={4}>
              <MetricCard
                title="Credits Consumed"
                value={formatNumber(totalCreditConsumed)}
                icon={<CreditCardOutlinedIcon sx={{ fontSize: "2.25rem" }} />}
                color="#22c55e"
                subtitle="Platform-wide consumption"
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};